import * as THREE from 'three';

/**
 * Sketch core data model — the freeform (Sketch-style) workbench document.
 *
 * Design language: a solid is an ANALYTIC shell, not a triangle soup. Every
 * face keeps its plane (origin + unit normal), its outer contour and its
 * hole contours as ordered 3D polylines; curves (circles/arcs) additionally
 * keep their center/radius/angles so inference and Push/Pull can rebuild
 * exact geometry. Triangulation is a render-time derivation (triangulate.ts),
 * never stored — pushing a face only ever rewrites the touched loops.
 *
 * This mirrors IngeTrazo's "hermetic solid" philosophy: a broken shell is
 * never committed (see pushPull.ts's watertight guard); the analytic form is
 * what makes per-face pushes cheap and hole punching decidable.
 */

/** Compact vector serializable into the .yap envelope. */
export type SketchVec3 = [number, number, number];

export type SketchCurveKind = 'line' | 'circle' | 'arc';

/**
 * One modeled edge: straight segment or discretized circle/arc. Edges are
 * DERIVED (deduplicated from face contours by topology.ts) and are not
 * stored on the solid — inference, selection and Erase operate on edges
 * resolved from the current face set.
 */
export interface SketchEdge {
    id: string;
    kind: SketchCurveKind;
    /** Dense polyline of the curve (2 points for lines). */
    points: SketchVec3[];
    /** Curve metadata for inference (circle/arc only). */
    center?: SketchVec3;
    normal?: SketchVec3;
    radius?: number;
    startAngle?: number;
    endAngle?: number;
}

/**
 * A LOOSE edge — a drawn line segment not (yet) belonging to any face.
 * Sketch semantics: edges are first-class; when connected loose edges close
 * a coplanar region, the region becomes a face and the consumed edges turn
 * into its contour (edgeGraph.detectClosedRegions).
 */
export interface SketchLooseEdge {
    id: string;
    a: SketchVec3;
    b: SketchVec3;
}

/**
 * One planar face of a solid. `outer` is CCW when seen from the normal side;
 * holes are CW. All contour points lie exactly on the plane through
 * `origin` with unit `normal`. Loops are stored OPEN: the first point is
 * NOT repeated at the end — the closing segment is implied (see topology.ts).
 */
export interface SketchFace {
    id: string;
    origin: SketchVec3;
    normal: SketchVec3;
    outer: SketchVec3[];
    holes: SketchVec3[][];
    /** Paint bucket target; falls back to the solid's material. */
    materialId?: string;
    /** Exact-curve definition of the outer loop when it is a circle/arc/polygon. */
    loopMeta?: SketchLoopMeta;
}

export interface SketchSolid {
    id: string;
    name: string;
    faces: SketchFace[];
    materialId: string;
    visible: boolean;
    locked: boolean;
    /** Owning group (groups.ts); undefined = loose top-level solid. */
    groupId?: string;
    /** Owning tag (Tags panel); undefined = untagged (always shown). */
    tagId?: string;
}

/** Tag (Sketch's layer): a named visibility switch over solids. */
export interface SketchTag {
    id: string;
    name: string;
    visible: boolean;
}

/**
 * Saved scene: a named camera + display-style snapshot (Scenes panel).
 * Applying restores the workbench view in one click.
 */
export interface SketchScene {
    id: string;
    name: string;
    camera: {
        position: [number, number, number];
        target: [number, number, number];
        fov: number;
    };
    displayStyle: SketchDisplayStyle;
}

/** Shadows panel settings (sun position + light levels, model state). */
export interface SketchShadowSettings {
    enabled: boolean;
    /** Day of year (1..365) — drives the seasonal sun arc. */
    day: number;
    /** Hour of day (0..24) — drives the daily sun arc. */
    hour: number;
    /** Ambient/light level (0..1, Sketch's Light slider). */
    light: number;
    /** Shadow darkness (0..1, Sketch's Dark slider). */
    dark: number;
}

/**
 * Exact-curve metadata for a face's outer loop. Loops are stored as
 * polylines (the boundary truth), but a loop that came from the Circle,
 * Polygon or Arc tool also carries its analytic definition so Push/Pull can
 * REGENERATE the lifted loop exactly (same center/radius/segment count —
 * not a copy of the base tessellation) and inference can snap to the true
 * center.
 */
export interface SketchLoopMeta {
    kind: 'circle' | 'arc' | 'polygon';
    center: SketchVec3;
    normal: SketchVec3;
    radius: number;
    /** arc/polygon: angle range (radians) / vertex count. */
    startAngle?: number;
    endAngle?: number;
    sides?: number;
    segments?: number;
}

/** Sketch-style group: a named container over solids. */
export interface SketchGroup {
    id: string;
    name: string;
    solidIds: string[];
}

/**
 * Component definition: a reusable named geometry template. Inserting a
 * definition clones its faces into a fresh solid (independent copy — shared
 * definition instances are a v2 step).
 */
export interface SketchComponentDef {
    id: string;
    name: string;
    /** Face templates (cloned with fresh ids on insert). */
    faces: SketchFace[];
    materialId: string;
}

/** Tape Measure output: infinite dashed guide line or a single guide point. */
export interface SketchGuide {
    id: string;
    kind: 'line' | 'point';
    a: SketchVec3;
    b?: SketchVec3;
}

/**
 * Dimension (Construction tools): a measured length between two model
 * points with an offset witness line. The committed value renders at the
 * dim line midpoint; `text` overrides the measured label when set.
 */
export interface SketchDimension {
    id: string;
    a: SketchVec3;
    b: SketchVec3;
    /** Dim line position (the offset the user picked away from a→b). */
    offset: SketchVec3;
    value: number;
    text?: string;
}

/** Text (Construction tools): a 2D label anchored to a model point. */
export interface SketchLabel {
    id: string;
    anchor: SketchVec3;
    /** Leader line end (screen-placed by the user). */
    leader: SketchVec3;
    text: string;
}

/** Custom drawing axes (Axes tool): replaces the Z=0 ground as the default plane. */
export interface SketchDrawingAxes {
    origin: SketchVec3;
    xAxis: SketchVec3;
    yAxis: SketchVec3;
}

/**
 * Display styles. The offered set mirrors the BIM view modes
 * (Wireframe / Hidden line / Shaded / Consistent colors / Realistic);
 * monochrome and xray stay valid for scenes saved with the older set.
 */
export type SketchDisplayStyle =
    | 'shaded'
    | 'hiddenLine'
    | 'monochrome'
    | 'wireframe'
    | 'xray'
    | 'consistentColors'
    | 'realistic';

/** Face identifier inside the composite selection id space. */
export const faceSelectionId = (solidId: string, faceId: string): string => `${solidId}:${faceId}`;

export const parseFaceSelectionId = (id: string): { solidId: string; faceId: string } | null => {
    const idx = id.indexOf(':');
    if (idx <= 0 || idx === id.length - 1) return null;
    return { solidId: id.slice(0, idx), faceId: id.slice(idx + 1) };
};

/**
 * Edge identifier inside the composite selection id space:
 * `solidId:edge:<topologyKey>`. The key is a SharedEdge id from
 * topology.ts (`ka~kb` of weld-keyed endpoints) — derived, never stored,
 * so a selection id only stays valid while the touched loops are intact.
 */
const EDGE_MARKER = ':edge:';

export const edgeSelectionId = (solidId: string, edgeKey: string): string => `${solidId}${EDGE_MARKER}${edgeKey}`;

export const parseEdgeSelectionId = (id: string): { solidId: string; edgeKey: string } | null => {
    const idx = id.indexOf(EDGE_MARKER);
    if (idx <= 0 || idx === id.length - EDGE_MARKER.length) return null;
    return { solidId: id.slice(0, idx), edgeKey: id.slice(idx + EDGE_MARKER.length) };
};

/**
 * Vertex identifier inside the composite selection id space:
 * `solidId:vertex:<pointKey>`. The key is a weld key from topology.ts
 * (`x|y|z` at topology epsilon) — derived from the contour points, never
 * stored, so a selection id only stays valid while a corner still exists at
 * that welded position.
 */
const VERTEX_MARKER = ':vertex:';

export const vertexSelectionId = (solidId: string, vertexKey: string): string => `${solidId}${VERTEX_MARKER}${vertexKey}`;

export const parseVertexSelectionId = (id: string): { solidId: string; vertexKey: string } | null => {
    const idx = id.indexOf(VERTEX_MARKER);
    if (idx <= 0 || idx === id.length - VERTEX_MARKER.length) return null;
    return { solidId: id.slice(0, idx), vertexKey: id.slice(idx + VERTEX_MARKER.length) };
};

/**
 * Loose edge identifier inside the composite selection id space:
 * `looseEdge:<edgeId>`. Unlike solid edges, loose edges are STORED entities
 * with stable ids, so the composite wraps the record id directly and stays
 * valid across endpoint stretches (no position-derived re-keying).
 */
const LOOSE_EDGE_MARKER = 'looseEdge:';

export const looseEdgeSelectionId = (edgeId: string): string => `${LOOSE_EDGE_MARKER}${edgeId}`;

export const parseLooseEdgeSelectionId = (id: string): string | null =>
    id.startsWith(LOOSE_EDGE_MARKER) ? id.slice(LOOSE_EDGE_MARKER.length) : null;

/** Plane helper for a face (recomputed on demand, never stored). */
export function facePlane(face: SketchFace): THREE.Plane {
    return new THREE.Plane(
        new THREE.Vector3(...face.normal).normalize(),
        -new THREE.Vector3(...face.origin).dot(new THREE.Vector3(...face.normal).normalize())
    );
}

/**
 * Effective solid visibility: the solid's own flag AND its tag's flag.
 * Single source of truth used by rendering, inference pools and panels.
 */
export function solidEffectiveVisible(
    solid: SketchSolid,
    tags: Record<string, SketchTag>
): boolean {
    if (!solid.visible) return false;
    if (solid.tagId) {
        const tag = tags[solid.tagId];
        if (tag && !tag.visible) return false;
    }
    return true;
}

/** Materialized world-space points of an edge's polyline. */
export function edgePoints(edge: SketchEdge): THREE.Vector3[] {
    return edge.points.map((p) => new THREE.Vector3(...p));
}
